import { useState } from "react";
import { WIN_PROOF_MAX_PDF } from "../../api/gigboard";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { FileButton } from "../../components/FileButton";
import { ScreenHeader } from "../../components/ScreenHeader";
import { WinFields, emptyWin, winError, type WinDraft } from "./WinFields";

/** Photos and screenshots are shrunk on the server, so only an unreasonably large one is refused here. */
const WIN_PROOF_MAX_IMAGE = 8 * 1024 * 1024;

/** Null when the file can be sent as proof, otherwise what to do instead. */
export function proofError(file: File): string | null {
  const isImage = /^image\//.test(file.type);
  const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
  if (!isImage && !isPdf) return "Proof must be a photo, a screenshot or a PDF.";
  if (isPdf && file.size > WIN_PROOF_MAX_PDF) {
    return "That PDF is too large (2.5 MB at most). Send a screenshot of it instead.";
  }
  if (isImage && file.size > WIN_PROOF_MAX_IMAGE) {
    return "That photo is too large (8 MB at most). Take a screenshot instead.";
  }
  return null;
}

/* Report a Win: any paid work from their skill counts toward Youth-in-Work, however it was found. */
export function ReportWinScreen({
  prefill,
  busy,
  error,
  onSave,
  onCancel,
}: {
  prefill?: Partial<WinDraft> | undefined;
  busy: boolean;
  error: string | null;
  onSave: (draft: WinDraft, proof: File | null) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(() => emptyWin(prefill));
  const [proof, setProof] = useState<File | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const save = () => {
    const invalid = winError(draft);
    setProblem(invalid);
    if (!invalid) onSave(draft, proof);
  };

  return (
    <div>
      <ScreenHeader
        kicker="Step 6 · Report a win"
        title="Tell us about your paid work."
        sub="Any paid work using your skill counts, however you found it: a gig platform, a friend, Instagram, a past client, a job or an internship."
      />
      <section className="my-4 rounded-[var(--radius-card)] border border-border bg-surface p-5">
        <WinFields value={draft} onChange={setDraft} />
        <div className="mb-4 flex flex-col gap-1.5">
          <span className="text-[12.5px] font-bold text-text">
            Proof (optional): a payment screenshot or contract
          </span>
          <FileButton
            label="Choose a file"
            ariaLabel="Proof (optional): a payment screenshot or contract"
            accept="image/*,application/pdf"
            hasFile={Boolean(proof)}
            disabled={busy}
            onFile={(file) => {
              const bad = proofError(file);
              setProblem(bad);
              setProof(bad ? null : file);
            }}
          />
          {proof ? <span className="text-[13px] text-text-muted">{proof.name}</span> : null}
        </div>
        {problem || error ? <Banner tone="bad">{problem ?? error}</Banner> : null}
        <div className="flex flex-wrap gap-2.5">
          <Button disabled={busy} onClick={save}>
            {busy ? "Saving..." : "Save my win"}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </section>
    </div>
  );
}
